/**
 * @file TableToolbarButtonView.tsx
 * @description Icon-only Filters / Columns chip for the scouting table
 * toolbar. Extracted from WatchlistToolbarView (SE-8439) so the Figma
 * `molecules/TableToolbarButton` component (Gemini UI System 270:121) has a
 * code component to bind through Code Connect. The chip is idle at 36×36
 * with the label on the native browser tooltip via `aria-label`; when a
 * count is present it grows inline to host the count badge.
 * @module components/molecules/TableToolbarButton
 */
import { Columns3Icon, FilterIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Which toolbar control the chip raises — mirrors the Figma `kind` variant. */
export type TableToolbarButtonKind = "filter" | "columns";

const KIND = {
  filter: { icon: FilterIcon, label: "Filters" },
  columns: { icon: Columns3Icon, label: "Columns" },
} as const;

const CHIP_ACTIVE_CLASS =
  "web:cursor-pointer web:hover:bg-status-info/25 h-9 flex-row items-center gap-1 rounded-sm border-2 border-status-info bg-status-info/15 px-2";
const CHIP_IDLE_CLASS =
  "web:cursor-pointer web:hover:bg-surface-raised size-9 items-center justify-center rounded-sm border border-outline-strong";

/** Props for {@link TableToolbarButtonView}. */
export interface TableToolbarButtonProps {
  /** Filters or Columns — picks the icon and the default accessible name. */
  readonly kind: TableToolbarButtonKind;
  /** Active selections; a positive count switches the chip to its active surface and shows the badge. */
  readonly count?: number;
  /** Raise the sheet / picker. */
  readonly onPress: () => void;
  /** Inert while the table is loading. */
  readonly disabled?: boolean;
  /** Overrides the kind's default accessible name. */
  readonly accessibilityLabel?: string;
  readonly testID?: string;
  /** Test identifier for the count badge — minted by the caller, never derived from `testID` (SE-8049). */
  readonly countTestID?: string;
  /** Web-only `data-*` markers (e.g. the outside-click guard on the column picker). */
  readonly domAttributes?: Readonly<Record<`data-${string}`, string>>;
}

/**
 * Icon-only toolbar chip with an optional count badge.
 * @param props - Component props
 * @param props.kind - Filters or Columns
 * @param props.count - Active selections
 * @param props.onPress - Press handler
 * @param props.disabled - Inert while loading
 * @param props.accessibilityLabel - Accessible-name override
 * @param props.testID - Test identifier
 * @param props.countTestID - Count badge test identifier
 * @param props.domAttributes - Web-only data attributes
 */
const TableToolbarButtonView = ({
  kind,
  count = 0,
  onPress,
  disabled = false,
  accessibilityLabel,
  testID,
  countTestID,
  domAttributes,
}: TableToolbarButtonProps) => (
  <Pressable
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel ?? KIND[kind].label}
    testID={testID}
    className={count > 0 ? CHIP_ACTIVE_CLASS : CHIP_IDLE_CLASS}
    {...domAttributes}
  >
    <Icon
      as={KIND[kind].icon}
      size="sm"
      className={
        count > 0 ? "text-status-info" : "text-needs-semantic-typography-800"
      }
    />
    {count > 0 ? (
      <Stack
        direction="horizontal"
        className="items-center justify-center rounded-sm bg-status-info px-1"
        testID={countTestID}
      >
        <Text variant="micro-strong" className="!text-content-inverse">
          {count}
        </Text>
      </Stack>
    ) : null}
  </Pressable>
);

TableToolbarButtonView.displayName = "TableToolbarButtonView";
export default memo(TableToolbarButtonView);
